// Small display pieces used on several screens.

import { useState, type CSSProperties } from 'react';
import type { RequestStatus, User } from '../types';
import { Icon } from './Icon';
import type { IconName } from './icons';

export const initials = (name: string | undefined): string =>
  (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

export const firstName = (name: string | undefined): string => (name || '').split(/\s+/)[0] || '';

export const byName = (a: Pick<User, 'name'>, b: Pick<User, 'name'>): number => a.name.localeCompare(b.name, 'en-GB');

export function Avatar({ name, photoUrl, className = '' }: {
  name: string | undefined; photoUrl?: string | null; className?: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const hasPhoto = !!photoUrl && photoUrl !== failedUrl;
  return (
    <span className={`avatar ${hasPhoto ? 'avatar-photo ' : ''}${className}`.trim()} aria-hidden="true">
      {hasPhoto
        ? <img src={photoUrl} alt="" width={32} height={32} loading="lazy" onError={() => setFailedUrl(photoUrl)} />
        : initials(name)}
    </span>
  );
}

const STATUS: Record<RequestStatus, { label: string; icon: IconName }> = {
  pending: { label: 'Pending', icon: 'clock' },
  approved: { label: 'Approved', icon: 'check-circle' },
  declined: { label: 'Declined', icon: 'x-circle' },
  cancelled: { label: 'Cancelled', icon: 'x' },
};

export function EmployeeName({ user }: { user: User | undefined }) {
  return (
    <span className="employee-identity">
      <Avatar name={user?.name} photoUrl={user?.photoUrl} />
      <span>{user?.name ?? 'Unknown'}</span>
    </span>
  );
}

export function StatusChip({ status }: { status: RequestStatus }) {
  const s = STATUS[status] ?? STATUS.pending;
  return (
    <span className={`chip chip-${status}`}>
      <Icon name={s.icon} size={14} />
      {s.label}
    </span>
  );
}

/** "1 person", "3 people". */
export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** Hands a leave type's colours to the stylesheet, which reads them as --c (fill) and --a (accent). */
export const typeStyle = (t: { color: string; accent: string }): CSSProperties =>
  ({ '--c': t.color, '--a': t.accent }) as CSSProperties;
